import { jsonapiApi } from '../api/jsonapi.js';
import { api } from '../api/api.js';
import { debugLogger } from '../helpers/debugLogger.js';
import { errorLogger } from '../helpers/errorLogger.js';


function deepEqual(obj1, obj2) {
  return JSON.stringify(obj1) === JSON.stringify(obj2);
}

const useShopStore = Pinia.defineStore('shop', {
  state() {
    return {
      cartToken: '',
      acceptGift: true,
      acceptGiftEdited: false,
      giftNamesList: {},
      giftDedicationsList: {},
      globalInformation: [],
      currentStage: 'Cart',
      currentDataStep: 'data',
      orderData: {
        data: {
          name: null,
          surname: null,
          email: null,
          phone: null,
          createAccount: null,
          status: null
        },
        address: {
          street: null,
          houseNumber: null,
          apartmentNumber: null,
          postal: null,
          city: null,
          country: null,
          status: null
        },
        payment: {
          paymentMethod: null,
          status: null
        },
        consents: {
          terms: null,
          email: null,
          phone: null,
          smail: null,
          status: null
        }
      },
      cartUUID: '',
      userUUID: '',
      userData: '',
      orderItems: [],
      suggestedItems: [],
      paymentOptions: [],
      isPaymentActive: false,
      isWaitingForProductToBeAddedToCart: false,
      loading: false,
      error: null
    }
  },
  persist: true,
  actions: {
    updateCurrentStage(stage) {
      this.currentStage = stage
    },
    updateCurrentDataStep(step) {
      this.currentDataStep = step
    },
    resetOrderData() {
      this.orderData =  {
        data: {
          name: null,
            surname: null,
            email: null,
            phone: null,
            createAccount: null,
            status: null
        },
        address: {
          street: null,
            houseNumber: null,
            apartmentNumber: null,
            postal: null,
            city: null,
            country: null,
            status: null
        },
        payment: {
          paymentMethod: null,
            status: null
        },
        consents: {
          terms: null,
            email: null,
            phone: null,
            smail: null,
            status: null
        }
      }
      this.giftNamesList = {}
      this.giftDedicationsList = {}
      this.acceptGift = true
      this.acceptGiftEdited = false
    },
    resetCart() {
      this.cartUUID = ''
      this.orderItems = []
      this.userData = []
      this.suggestedItems = []
      this.giftNamesList = {}
      this.giftDedicationsList = {}
      this.acceptGift = true
      this.acceptGiftEdited = false
      this.resetOrderData()
    },
    async fetchGlobalInformation() {
      this.loading = true;
      this.error = null;
      try {
        const globalInformation = await jsonapiApi.getGlobalInformation();

        if (globalInformation) {
          debugLogger('globalInformation', globalInformation)
          this.globalInformation = globalInformation;
        }
      } catch (error) {
        this.error = 'Failed to fetch globalInformation';
        errorLogger('Error fetching globalInformation:', error);
      } finally {
        this.loading = false;
      }
    },
    async fetchUserUUID() {
      this.loading = true;
      this.error = null;
      try {
        const userUUID = await jsonapiApi.getUserUUID();

        if (userUUID) {
          debugLogger('userUUID', userUUID)
          this.userUUID = userUUID;
        } else {
          debugLogger('userUUID empty')
          this.userUUID = '';
        }
      } catch (error) {
        this.error = 'Failed to fetch user UUID';
        errorLogger('Error fetching user UUID:', error);
      } finally {
        this.loading = false;
      }
    },
    async fetchUser() {
      this.loading = true;
      this.error = null;
      try {
        if (this.userUUID) {
          const userData = await jsonapiApi.getUser(this.userUUID);

          if (userData) {
            debugLogger('userData', userData)
            this.userData = userData;
          } else {
            this.userData = '';
          }
        }
      } catch (error) {
        this.error = 'Failed to fetch user data';
        errorLogger('Error fetching user data:', error);
      } finally {
        this.loading = false;
      }
    },
    async fetchCart() {
      this.loading = true;
      this.error = null;
      try {
        const cartData = await jsonapiApi.getCart();

        if (cartData && cartData['0']) {
          debugLogger('cart: ', cartData);
          this.cartUUID = cartData['0'].id;
          this.acceptGift = cartData['0'].field_accept_gift_package

          if (cartData['0'].order_items) {
            const newOrderItems = cartData['0'].order_items.map(item => ({
              id: item.id,
              type: item.type,
              total_price: item.total_price,
              unit_price: item.unit_price,
              quantity: item.quantity,
              purchased_entity: item.purchased_entity,
              payment_type: item.field_payment_type,
              gift: item.field_gift,
              meta_payload: item.field_meta_payload,
              category: item.field_category
            }));

            // Loop through newOrderItems with a for...of loop
            for (const newItem of newOrderItems) {
              const existingItem = this.orderItems.find(item => item.id === newItem.id);
              if (existingItem) {
                // Check each property and update only if there's a difference
                Object.keys(newItem).forEach(key => {
                  if (!deepEqual(existingItem[key], newItem[key])) {
                    this.$patch(() => {
                      existingItem[key] = newItem[key];
                    });
                  }
                });
              } else {
                // If the item does not exist, add it to the orderItems
                const productData = await jsonapiApi.getProduct(newItem.purchased_entity.product_id.id, newItem.purchased_entity.product_id.type);
                debugLogger('productData', productData);
                newItem.product = productData;
                this.orderItems.push(newItem);
              }
            }

            // Remove items that no longer exist in the new data
            this.orderItems = this.orderItems.filter(item => newOrderItems.some(newItem => newItem.id === item.id));

            // Get new cart suggestions
            this.suggestedItems = await api.getCartSuggestions(this.cartUUID)

            // Get user data
            this.fetchUserUUID().then(() => this.fetchUser());

          } else {
            this.userData = [];
            this.resetOrderData()
            this.orderItems = [];
            this.suggestedItems = [];
          }
        } else {
          this.resetCart()
        }
      } catch (error) {
        this.error = 'Failed to fetch cart data';
        errorLogger('Error fetching cart data:', error);
      } finally {
        this.loading = false;
      }
    },
    async deleteOrderItem(itemUUID) {
      this.loading = true;
      this.error = null;
      try {
        const response = await jsonapiApi.deleteOrderItem(this.cartUUID, itemUUID);
        debugLogger('delete response', response)
        await this.fetchCart()
      } catch (error) {
        this.error = 'Failed to delete order item';
        errorLogger('Error deleting order item:', error);
      } finally {
        this.loading = false;
      }
    },
    async patchOrderItem(itemUUID, itemType, object) {
      this.loading = true;
      this.error = null;
      try {
        const data = {
          "type": itemType,
          "id": itemUUID,
          "attributes": {}
        }

        if (object.gift !== null && object.gift !== undefined) {
          data.attributes.field_gift = object.gift
        }

        if (object.quantity !== null && object.quantity !== undefined) {
          data.attributes.quantity = object.quantity
        }

        if (object.field_meta_payload && object.field_meta_payload.length) {
          data.attributes.field_meta_payload = object.field_meta_payload
        }

        debugLogger('patch order item data', data)
        const response = await jsonapiApi.patchOrderItem(this.cartUUID, itemUUID, data);
        debugLogger('patch order item response', response)
        await this.fetchCart()
      } catch (error) {
        this.error = 'Failed to patch order item';
        errorLogger('Error patching order item:', error);
      } finally {
        this.loading = false;
      }
    },
    async patchOrder(object) {
      this.loading = true;
      this.error = null;
      try {
        const data = {
          "type": "commerce-order--default",
          "id": this.cartUUID
        }

        if (object) {
          data.attributes = object
        }

        debugLogger('patch order data', data)
        const response = await jsonapiApi.patchOrder(this.cartUUID, data);
        debugLogger('patch order response', response)
        await this.fetchCart()
        return response
      } catch (error) {
        this.error = 'Failed to patch order';
        errorLogger('Error patching order:', error);
      } finally {
        this.loading = false;
      }
    },
    async addProductToCart(payload, redirectToCart = false, blockAddToCartButton = null){
      this.isWaitingForProductToBeAddedToCart = true
      this.isPaymentActive = false
      this.loading = true;
      this.error = null;
      try {
        const response = await jsonapiApi.addProductToCart(payload);

        debugLogger('add product to cart response', response)
      } catch (error) {
        this.error = 'Failed to add product to cart';
        errorLogger('Error adding product to cart:', error);
      } finally {
        this.loading = false

        if (redirectToCart) {
          window.location.href = Drupal.url('koszyk')
        }

        if (blockAddToCartButton) {
          blockAddToCartButton.value = false
        }
      }
    },
    async getPaymentOptions() {
      this.loading = true;
      this.error = null;
      try {
        const paymentOptions = await api.getPaymentOptions(this.cartUUID);

        if (paymentOptions) {
          this.paymentOptions = paymentOptions;
        } else {
          this.paymentOptions = [];
        }
      } catch (error) {
        this.error = 'Failed to fetch payment options';
        errorLogger('Error fetching payment options:', error);
      } finally {
        this.loading = false;
      }
    },
    async placeOrder() {
      this.loading = true;
      this.error = null;
      try {
        const placedOrder = await api.setOrderStateToValidation(this.cartUUID);

        if (placedOrder) {
          debugLogger('placedOrder', placedOrder)
          this.isPaymentActive = true
          return placedOrder
        }
      } catch (error) {
        this.error = 'Failed to place order';
        errorLogger('Error placing order:', error);
      } finally {
        this.loading = false;
      }
    }
  }
})

export {
  useShopStore
}
